import { Component, Output, EventEmitter, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { Customer, CustomerService } from '../../services/customer';
import { FormsModule } from '@angular/forms';
import { SearchBarComponent } from '../search-bar.component/search-bar-component';
import { MatDialog } from '@angular/material/dialog';
import { CustomerDetailsDialogComponent } from '../shared/customer-details-dialog/customer-details-dialog';
import { DeliveryService } from '../../services/delivery.service';
import { Router } from '@angular/router';

export type CustomerLoadMode = 'ask' | 'recent' | 'last_invoice' | 'none';

export interface CustomerSelectedEvent {
  customer: Customer;
  loadMode?: CustomerLoadMode;
}

@Component({
  selector: 'app-customer-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatIconModule,
    SearchBarComponent
  ],
  templateUrl: './customer-list.component.html',
  styleUrls: ['./customer-list.component.scss']
})
export class CustomerListComponent implements OnInit {
  @Input() routeIds: number[] | null = null; // To pass routeId from the parent component
  @Output() customerSelected = new EventEmitter<CustomerSelectedEvent | null>();

  selectedCustomerId: number | null = null;
  customers: Customer[] = [];
  filteredCustomers: Customer[] = [];
  searchTerm: string = '';

  constructor(
    private customerService: CustomerService,
    private dialog: MatDialog,
    private deliveryService: DeliveryService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.customerService.get().subscribe(customers => {
      this.customers = customers;
      this.filterByRouteId();
      this.filteredCustomers = [...this.customers];
      this.hydrateCustomerBalances(this.customers);
    });
  }

  toggleSelection(customer: Customer): void {
    const wasSelected = this.selectedCustomerId === customer.id;
    this.selectedCustomerId = wasSelected ? null : customer.id;
    this.customerSelected.emit(wasSelected ? null : { customer, loadMode: 'ask' });
  }

  isSelected(customerId: number): boolean {
    return this.selectedCustomerId === customerId;
  }

  searchCustomers(): void {
    // Filter customers based on both search term and routeId
    this.filteredCustomers = this.customers.filter(customer =>
      customer.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      (customer.phone && customer.phone.includes(this.searchTerm))
    );
  }

  // Filter customers based on routeId
  filterByRouteId(): void {
    if (this.routeIds?.length) {
      console.log(`Customers Count:  ${this.customers.length}`);
      this.customers = this.customers.filter(customer => {
        const customerSegmentIds = customer.routeIds?.length
          ? customer.routeIds
          : (customer.routeId ? [customer.routeId] : []);
        return customerSegmentIds.some(id => this.routeIds?.includes(id));
      });
    }
  }

  createCustomer(): void {
    this.router.navigate(['/customers/new']);
  }

  openCustomerDetails(event: Event, customer: Customer): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(CustomerDetailsDialogComponent, {
      data: { customer: customer, showSelect: true },
      width: '94vw',
      maxWidth: '520px',
      maxHeight: '90vh',
      hasBackdrop: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.select === true) {
        this.selectCustomer(customer, result.loadMode || 'ask');
      }
    });
  }

  selectCustomer(customer: Customer, loadMode: CustomerLoadMode = 'ask'): void {
    this.selectedCustomerId = customer.id;
    this.customerSelected.emit({ customer, loadMode });
  }

  private hydrateCustomerBalances(customers: Customer[]): void {
    for (const customer of customers || []) {
      if (customer.id <= 0 || customer.pendingSync) continue;
      this.deliveryService.getCustomerBalance(customer.id).subscribe({
        next: res => {
          if (res.status === 'success' && res.balance) {
            customer.outstandingBalance = res.balance.netBalance;
            customer.currency = res.balance.currency;
          }
        },
        error: () => {
          // Keep the cached value if balance refresh is unavailable/offline.
        }
      });
    }
  }
}
